import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatScenarioPrice } from '../../utils/elasticityCalculations';

/**
 * Shared Simulation Settings Component
 * Used by both Screener and Watchlist modules
 * Allows users to adjust Days to Exit and Underlying Gain % parameters
 * and recalculate option values client-side.
 * Auto-updates the parent table on slider changes (debounced).
 */
const SimulationSettingsPanel = ({
  maxDays = 365,
  maxExpirationDate = null,
  onRefresh,
  onReset,
  isActive = false,
  defaultDays = null,
  defaultGain = null,
  contractCount = 0,
  label = 'All',
  defaultExpanded = false,
  externalDays = null, // Sync slider when days change externally (e.g., from heatmap view)
  // Optional: Days to Exit mode toggle
  daysToExitMode = null, // 'per-contract' | 'fixed' | 'percent' | null (no toggle)
  daysToExitPercent = null, // 0-1 float
  onModeChange = null,
  onPercentChange = null,
  // Underlying Gain controls. Default to the original coarse ladder + integer
  // slider step so every existing caller (Watchlist, Basic/Intermediate) is
  // unchanged; Intraday passes a fine 0.5%-to-±3% ladder and a 0.5 step.
  hotButtons = [-50, -35, -20, -10, -5, 0, 5, 10, 15, 20, 35, 50, 100, 200, 1000],
  gainStep = 1,
  // Time-to-exit unit toggle (intraday only). daysToTarget stays in fractional
  // DAYS; this only changes how the slider presents/steps it.
  showUnitToggle = false,
  daysToExitUnit = 'days',
  onUnitChange = null,
  maxFractionalDays = null,
  // Underlying price shared by every contract in the result set (0DTE only,
  // where one ticker is analysed at a time). When set, the gain readout and hot
  // buttons also show the price each move implies. Null for the Watchlist and
  // the Basic/Intermediate screener, which render the percentages alone.
  scenarioBasePrice = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [daysToTarget, setDaysToTarget] = useState(defaultDays || Math.min(45, maxDays));
  const [underlyingGain, setUnderlyingGain] = useState(defaultGain || 10);

  // Whether the mode toggle is available
  const hasModeToggle = onModeChange && onPercentChange;
  const isPercentMode = daysToExitMode === 'percent';

  // Debounced auto-refresh: fires onRefresh automatically when sliders change
  const debounceRef = useRef(null);
  const autoRefresh = useCallback((days, gain) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onRefresh) onRefresh(days, gain);
    }, 80);
  }, [onRefresh]);

  // Auto-refresh when days or gain changes (the core auto-update behavior)
  useEffect(() => {
    // Only auto-refresh if the panel has been interacted with (not on initial mount defaults)
    if (isActive || daysToTarget !== (defaultDays || Math.min(45, maxDays)) || underlyingGain !== (defaultGain || 10)) {
      autoRefresh(daysToTarget, underlyingGain);
    }
  }, [daysToTarget, underlyingGain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync slider when days change externally (e.g., from heatmap view)
  useEffect(() => {
    if (externalDays !== null && externalDays !== daysToTarget) {
      setDaysToTarget(externalDays);
    }
  }, [externalDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update days when maxDays changes
  useEffect(() => {
    if (daysToTarget > maxDays) {
      setDaysToTarget(maxDays);
    }
  }, [maxDays, daysToTarget]);

  // Track if values have changed from defaults
  const hasChanges = (() => {
    const daysChanged = defaultDays !== null && daysToTarget !== (defaultDays || Math.min(45, maxDays));
    const gainChanged = defaultGain !== null && underlyingGain !== (defaultGain || 10);
    const modeChanged = isPercentMode;
    return daysChanged || gainChanged || modeChanged;
  })();

  const handleReset = () => {
    setDaysToTarget(defaultDays || Math.min(45, maxDays));
    setUnderlyingGain(defaultGain || 10);
    if (onReset) {
      onReset();
    }
  };

  const handleHotButton = (value) => {
    setUnderlyingGain(value);
  };

  const formatGainLabel = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate exit date based on days to exit
  const getTargetDate = (days) => {
    const today = new Date();
    const targetDate = new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
    return targetDate;
  };

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date display with days
  const formatDateWithDays = (date, days) => {
    return `${formatDate(date)} (${days} d.)`;
  };

  // Get formatted target date display
  const getFormattedTargetDate = () => {
    const targetDate = getTargetDate(daysToTarget);
    return formatDateWithDays(targetDate, daysToTarget);
  };

  // Get formatted max expiration date display
  const getFormattedMaxDate = () => {
    if (maxExpirationDate) {
      const date = new Date(maxExpirationDate);
      return formatDateWithDays(date, maxDays);
    }
    // Fallback to calculating from days if date not provided
    const maxDate = getTargetDate(maxDays);
    return formatDateWithDays(maxDate, maxDays);
  };

  // Fixed days-to-exit slider config — original whole-day slider by default;
  // intraday (showUnitToggle) switches units while keeping daysToTarget in
  // fractional days. Non-intraday keeps the exact original min/max/step/fill.
  const fmtHM = (hours) => {
    const totalMin = Math.max(0, Math.round(hours * 60));
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };
  // Absolute simulated-sale datetime = now + hold (fractional days), shown in ET
  // since the whole intraday feature is anchored to the 16:00-ET close.
  const fmtExitDateTime = (fracDays) => {
    const d = new Date(Date.now() + fracDays * 86400000);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) + ' ET';
  };
  const fracMax = (maxFractionalDays != null ? maxFractionalDays : maxDays) || 1;
  let slMin = 1, slMax = maxDays, slStep = 1, slVal = daysToTarget, slToDays = (v) => v;
  let slLeft = '1 d.', slMid = `${Math.floor(maxDays / 2)} d.`, slRight = getFormattedMaxDate();
  let fixedHeaderLabel = getFormattedTargetDate();
  if (showUnitToggle && daysToExitUnit === 'hours') {
    slMin = 0; slMax = Math.max(1, Math.floor(fracMax * 24)); slStep = 1;
    slVal = Math.min(daysToTarget * 24, slMax); slToDays = (v) => v / 24;
    slLeft = '0h'; slMid = `${Math.round(slMax / 2)}h`; slRight = `${slMax}h`;
    fixedHeaderLabel = `${fmtHM(daysToTarget * 24)} → ${fmtExitDateTime(daysToTarget)}`;
  } else if (showUnitToggle && daysToExitUnit === 'min30') {
    slMin = 0; slMax = Math.max(0.5, fracMax * 24); slStep = 0.5;
    slVal = Math.min(daysToTarget * 24, slMax); slToDays = (v) => v / 24;
    slLeft = '0'; slMid = fmtHM(slMax / 2); slRight = fmtHM(slMax);
    fixedHeaderLabel = `${fmtHM(daysToTarget * 24)} → ${fmtExitDateTime(daysToTarget)}`;
  } else if (showUnitToggle) {
    slMin = 0; slMax = Math.max(1, Math.ceil(fracMax));
    slVal = Math.min(daysToTarget, slMax);
    slLeft = '0 d.'; slMid = `${Math.round(slMax / 2)} d.`; slRight = `${slMax} d.`;
    fixedHeaderLabel = daysToTarget < 1 ? `${fmtHM(daysToTarget * 24)} → ${fmtExitDateTime(daysToTarget)}` : getFormattedTargetDate();
  }
  const slFillPct = showUnitToggle
    ? (slMax > slMin ? ((slVal - slMin) / (slMax - slMin)) * 100 : 0)
    : (daysToTarget / maxDays) * 100;

  return (
    <div className="simulation-settings bg-gray-800 rounded-lg mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-200">
            Simulation Settings
          </h3>
          <span className="text-xs text-gray-400">
            {isActive ? (
              <span className="text-blue-500">
                Active ({isPercentMode
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of exp`
                  : `${daysToTarget} days`
                }, {formatGainLabel(underlyingGain)})
              </span>
            ) : (
              `${label} (${contractCount} contracts)`
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && isActive && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
              className="px-3 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
            >
              Reset
            </button>
          )}
          <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            ›
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Days to Exit Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-300">
                  Days to Exit
                </label>
                {/* Mode Toggle (only when screener provides the props) */}
                {hasModeToggle && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        onModeChange('fixed');
                      }}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        !isPercentMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      Fixed Days
                    </button>
                    <button
                      onClick={() => onPercentChange(daysToExitPercent || 0.30)}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        isPercentMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      % of Exp
                    </button>
                  </div>
                )}
                {/* Unit toggle (intraday only, fixed mode) */}
                {showUnitToggle && onUnitChange && !isPercentMode && (
                  <div className="flex gap-1">
                    {[['days', 'Days'], ['hours', 'Hours'], ['min30', '30-min']].map(([u, lbl]) => (
                      <button
                        key={u}
                        onClick={() => onUnitChange(u)}
                        className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                          daysToExitUnit === u
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-sm font-bold text-blue-500">
                {isPercentMode
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of expiration`
                  : fixedHeaderLabel
                }
              </span>
            </div>

            {isPercentMode ? (
              /* Percentage slider: 5% to 100% */
              <div className="relative">
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={Math.round((daysToExitPercent || 0.30) * 100)}
                  onChange={(e) => onPercentChange(Number(e.target.value) / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      ((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100
                    }%, #374151 ${((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            ) : (
              /* Fixed days slider — unit-aware in intraday, original otherwise */
              <div className="relative">
                <input
                  type="range"
                  min={slMin}
                  max={slMax}
                  step={slStep}
                  value={slVal}
                  onChange={(e) => setDaysToTarget(slToDays(Number(e.target.value)))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${slFillPct}%, #374151 ${slFillPct}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{slLeft}</span>
                  <span>{slMid}</span>
                  <span>{slRight}</span>
                </div>
              </div>
            )}
          </div>

          {/* Underlying Gain Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-300">
                Underlying Gain
              </label>
              <span className="text-sm font-bold text-blue-500">
                {formatGainLabel(underlyingGain)}
                {formatScenarioPrice(underlyingGain, scenarioBasePrice) && (
                  <span className="ml-1.5 font-normal text-gray-400">
                    → {formatScenarioPrice(underlyingGain, scenarioBasePrice)}
                  </span>
                )}
              </span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="-90"
                max="200"
                step={gainStep}
                value={Math.min(underlyingGain, 200)}
                onChange={(e) => setUnderlyingGain(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #218aff 0%, #218aff ${
                    underlyingGain > 200
                      ? 100  // If value exceeds slider max, show fully filled
                      : ((Math.min(underlyingGain, 200) + 90) / 290) * 100
                  }%, #374151 ${
                    underlyingGain > 200
                      ? 100  // If value exceeds slider max, show fully filled
                      : ((Math.min(underlyingGain, 200) + 90) / 290) * 100
                  }%, #374151 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-90%</span>
                <span>+55%</span>
                <span>+200%</span>
              </div>
            </div>

            {/* Hot Buttons */}
            <div className="flex flex-wrap gap-1 mt-3">
              {hotButtons.map((value) => {
                const hotPrice = formatScenarioPrice(value, scenarioBasePrice);
                return (
                  <button
                    key={value}
                    onClick={() => handleHotButton(value)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      hotPrice ? 'flex flex-col items-center leading-tight' : ''
                    } ${
                      underlyingGain === value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <span>{formatGainLabel(value)}</span>
                    {hotPrice && (
                      <span className={`text-[10px] ${underlyingGain === value ? 'text-blue-100' : 'text-gray-400'}`}>
                        {hotPrice}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reset Button */}
          {(isActive || hasChanges) && (
            <div className="pt-2">
              <button
                onClick={handleReset}
                className="w-1/3 px-4 py-2 text-sm font-medium rounded transition-colors bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Custom Slider Styles */}
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
        }
      `}</style>
    </div>
  );
};

export default SimulationSettingsPanel;
